import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/pt-br';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@shared/utils';

dayjs.extend(customParseFormat);
dayjs.locale('pt-br');

export interface DateRange {
  from?: string | undefined;
  to?: string | undefined;
}

export interface DateRangePickerWithPresetsProps {
  readonly value?: DateRange;
  readonly onChange?: (range: DateRange) => void;
  readonly disabled?: boolean;
  readonly className?: string;
}

type PeriodPreset =
  | 'yesterday'
  | 'today'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'last15days'
  | 'last3months'
  | 'custom';

const PRESETS: { value: PeriodPreset; label: string }[] = [
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: 'thisWeek', label: 'Esta Semana' },
  { value: 'lastWeek', label: 'Semana Passada' },
  { value: 'thisMonth', label: 'Este Mês' },
  { value: 'last15days', label: 'Últimos 15 dias' },
  { value: 'last3months', label: 'Últimos 3 meses' },
  { value: 'custom', label: 'Personalizado' },
];

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function getPresetDates(preset: PeriodPreset): DateRange {
  const today = dayjs();
  switch (preset) {
    case 'today':
      return { from: today.format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
    case 'yesterday': {
      const y = today.subtract(1, 'day');
      return { from: y.format('YYYY-MM-DD'), to: y.format('YYYY-MM-DD') };
    }
    case 'thisWeek':
      return { from: today.startOf('week').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
    case 'lastWeek': {
      const s = today.subtract(1, 'week').startOf('week');
      return { from: s.format('YYYY-MM-DD'), to: s.endOf('week').format('YYYY-MM-DD') };
    }
    case 'thisMonth':
      return { from: today.startOf('month').format('YYYY-MM-DD'), to: today.format('YYYY-MM-DD') };
    case 'last15days':
      return {
        from: today.subtract(15, 'day').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
    case 'last3months':
      return {
        from: today.subtract(3, 'month').format('YYYY-MM-DD'),
        to: today.format('YYYY-MM-DD'),
      };
    case 'custom':
      return {};
  }
}

function getRangeDayClass(isEndpoint: boolean, isInRange: boolean, isToday: boolean): string {
  if (isEndpoint) return 'bg-[#003B6A] text-white';
  if (isInRange) return 'bg-[#003B6A]/10 text-[#003B6A]';
  if (isToday) return 'border border-[#003B6A] text-[#003B6A]';
  return 'text-login-text-secondary hover:bg-[#F3F4F6]';
}

// Formats free-typed digits into a dd/mm/aa mask, mirroring DateInput's behavior.
function maskDateText(text: string): string {
  const digits = text.replace(/\D/g, '');
  let formatted = '';
  if (digits.length > 0) formatted = digits.slice(0, 2);
  if (digits.length > 2) formatted += '/' + digits.slice(2, 4);
  if (digits.length > 4) formatted += '/' + digits.slice(4, 6);
  return formatted;
}

export function DateRangePickerWithPresets({
  value,
  onChange,
  disabled,
  className,
}: DateRangePickerWithPresetsProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [localRange, setLocalRange] = useState<DateRange>(value ?? {});
  const [activePreset, setActivePreset] = useState<PeriodPreset | null>(null);
  const [leftMonth, setLeftMonth] = useState(() => {
    if (value?.from) return dayjs(value.from);
    return dayjs();
  });
  const [selecting, setSelecting] = useState<'from' | 'to'>('from');
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const [fromText, setFromText] = useState(() =>
    value?.from ? dayjs(value.from).format('DD/MM/YY') : '',
  );
  const [toText, setToText] = useState(() => (value?.to ? dayjs(value.to).format('DD/MM/YY') : ''));
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const rightMonth = leftMonth.add(1, 'month');

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing local range with external value change is intentional
    setLocalRange(value ?? {});
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing typed text with external value change is intentional
    setFromText(value?.from ? dayjs(value.from).format('DD/MM/YY') : '');
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing typed text with external value change is intentional
    setToText(value?.to ? dayjs(value.to).format('DD/MM/YY') : '');
  }, [value]);

  const handleFromTextChange = (text: string) => {
    const formatted = maskDateText(text);
    setFromText(formatted);
    const digits = formatted.replace(/\D/g, '');
    if (digits.length === 6) {
      const parsed = dayjs(formatted, 'DD/MM/YY');
      if (parsed.isValid()) {
        const dateStr = parsed.format('YYYY-MM-DD');
        setLocalRange({ ...localRange, from: dateStr });
        setActivePreset('custom');
        setLeftMonth(parsed);
      }
    } else if (digits.length === 0) {
      setLocalRange({ ...localRange, from: undefined });
    }
  };

  const handleToTextChange = (text: string) => {
    const formatted = maskDateText(text);
    setToText(formatted);
    const digits = formatted.replace(/\D/g, '');
    if (digits.length === 6) {
      const parsed = dayjs(formatted, 'DD/MM/YY');
      if (parsed.isValid()) {
        const dateStr = parsed.format('YYYY-MM-DD');
        setLocalRange({ ...localRange, to: dateStr });
        setActivePreset('custom');
      }
    } else if (digits.length === 0) {
      setLocalRange({ ...localRange, to: undefined });
    }
  };

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const popoverHeight = 380;
      const spaceBelow = window.innerHeight - rect.bottom;

      if (spaceBelow >= popoverHeight) {
        // Open below (default)
        setPopoverStyle({
          position: 'fixed',
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
          zIndex: 9999,
        });
      } else {
        // Not enough space below, open above
        setPopoverStyle({
          position: 'fixed',
          bottom: window.innerHeight - rect.top + 4,
          right: window.innerWidth - rect.right,
          zIndex: 9999,
        });
      }
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePresetClick = (preset: PeriodPreset) => {
    setActivePreset(preset);
    if (preset === 'custom') {
      setSelecting('from');
      return;
    }
    const dates = getPresetDates(preset);
    setLocalRange(dates);
    setFromText(dates.from ? dayjs(dates.from).format('DD/MM/YY') : '');
    setToText(dates.to ? dayjs(dates.to).format('DD/MM/YY') : '');
    if (dates.from) setLeftMonth(dayjs(dates.from));
  };

  const handleDayClick = (date: dayjs.Dayjs) => {
    setActivePreset('custom');
    const dateStr = date.format('YYYY-MM-DD');
    if (selecting === 'from') {
      const newRange: DateRange = { from: dateStr, to: undefined };
      setLocalRange(newRange);
      setFromText(date.format('DD/MM/YY'));
      setToText('');
      setSelecting('to');
    } else {
      let newRange: DateRange;
      if (localRange.from && dateStr < localRange.from) {
        newRange = { from: dateStr, to: localRange.from };
      } else {
        newRange = { from: localRange.from, to: dateStr };
      }
      setLocalRange(newRange);
      setFromText(newRange.from ? dayjs(newRange.from).format('DD/MM/YY') : '');
      setToText(newRange.to ? dayjs(newRange.to).format('DD/MM/YY') : '');
      setSelecting('from');
    }
  };

  const handleApply = () => {
    onChange?.(localRange);
    setIsOpen(false);
  };

  const handleClear = () => {
    setLocalRange({});
    setFromText('');
    setToText('');
    setActivePreset(null);
    setSelecting('from');
    onChange?.({});
    setIsOpen(false);
  };

  const fromDate = localRange.from ? dayjs(localRange.from) : null;
  const toDate = localRange.to ? dayjs(localRange.to) : null;

  function renderCalendar(month: dayjs.Dayjs) {
    const daysInMonth = month.daysInMonth();
    const firstDayOfWeek = month.startOf('month').day();
    const days: (dayjs.Dayjs | null)[] = [];
    for (let i = 0; i < firstDayOfWeek; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(month.date(d));

    return (
      <div className="flex flex-col">
        <span className="mb-2 text-center text-sm font-semibold text-login-text-secondary capitalize">
          {month.format('MMMM YYYY')}
        </span>
        <div className="grid grid-cols-7 gap-0.5 text-center">
          {WEEKDAYS.map((day) => (
            <span
              key={`${month.format('MM')}-${day}`}
              className="py-1 text-[10px] font-semibold text-[#71767F]"
            >
              {day}
            </span>
          ))}
          {days.map((day, idx) => {
            if (day === null) return <span key={`empty-${month.format('MM')}-${String(idx)}`} />;
            const dateStr = day.format('YYYY-MM-DD');
            const isFrom = fromDate !== null && day.isSame(fromDate, 'day');
            const isTo = toDate !== null && day.isSame(toDate, 'day');
            const isInRange =
              fromDate !== null &&
              toDate !== null &&
              day.isAfter(fromDate, 'day') &&
              day.isBefore(toDate, 'day');
            const isToday = day.isSame(dayjs(), 'day');

            return (
              <button
                key={dateStr}
                type="button"
                onClick={() => handleDayClick(day)}
                className={cn(
                  'h-7 w-7 rounded text-xs font-medium transition-colors',
                  getRangeDayClass(isFrom || isTo, isInRange, isToday),
                )}
              >
                {day.date()}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      {/* Trigger - Two typeable date inputs side by side */}
      <div ref={triggerRef} className="flex items-center gap-2">
        <div
          className={cn(
            'flex h-10 flex-1 items-center gap-2 rounded-md border bg-white px-3',
            'hover:border-login-border-brand transition-colors border-login-border-primary',
            'has-focus-visible:ring-2 has-focus-visible:ring-brand has-focus-visible:ring-offset-1',
            disabled ? 'cursor-not-allowed opacity-60' : '',
          )}
        >
          <CalendarDaysIcon className="size-5 shrink-0 text-[#9CA3AF]" />
          <input
            type="text"
            value={fromText}
            onChange={(e) => handleFromTextChange(e.target.value)}
            onFocus={() => {
              setSelecting('from');
              if (!disabled) setIsOpen(true);
            }}
            disabled={disabled}
            placeholder="De"
            maxLength={8}
            aria-label="Data inicial"
            className={cn(
              'w-full bg-transparent text-sm text-login-text-secondary outline-hidden',
              'placeholder:text-[#A4A7AE] disabled:cursor-not-allowed',
            )}
          />
        </div>
        <span className="text-xs text-[#71767F]">—</span>
        <div
          className={cn(
            'flex h-10 flex-1 items-center gap-2 rounded-md border bg-white px-3',
            'hover:border-login-border-brand transition-colors border-login-border-primary',
            'has-focus-visible:ring-2 has-focus-visible:ring-brand has-focus-visible:ring-offset-1',
            disabled ? 'cursor-not-allowed opacity-60' : '',
          )}
        >
          <CalendarDaysIcon className="size-5 shrink-0 text-[#9CA3AF]" />
          <input
            type="text"
            value={toText}
            onChange={(e) => handleToTextChange(e.target.value)}
            onFocus={() => {
              setSelecting('to');
              if (!disabled) setIsOpen(true);
            }}
            disabled={disabled}
            placeholder="Até"
            maxLength={8}
            aria-label="Data final"
            className={cn(
              'w-full bg-transparent text-sm text-login-text-secondary outline-hidden',
              'placeholder:text-[#A4A7AE] disabled:cursor-not-allowed',
            )}
          />
        </div>
      </div>

      {/* Popover */}
      {isOpen
        ? createPortal(
            <div
              ref={containerRef}
              style={popoverStyle}
              className="flex rounded-lg border border-[#E5E7EB] bg-white shadow-xl"
            >
              {/* Left: Presets */}
              <div className="flex w-40 flex-col border-r border-[#E5E7EB] py-2">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.value}
                    type="button"
                    onClick={() => handlePresetClick(preset.value)}
                    className={cn(
                      'px-4 py-2 text-left text-sm transition-colors',
                      activePreset === preset.value
                        ? 'bg-[#003B6A]/10 font-semibold text-[#003B6A]'
                        : 'text-login-text-secondary hover:bg-[#F3F4F6]',
                    )}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Right: Calendars */}
              <div className="flex flex-col p-4">
                {/* Navigation */}
                <div className="mb-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setLeftMonth(leftMonth.subtract(1, 'month'))}
                    className="flex h-7 w-7 items-center justify-center rounded hover:bg-[#F3F4F6]"
                    aria-label="Mês anterior"
                  >
                    <ChevronLeftIcon className="h-4 w-4 text-login-text-secondary" />
                  </button>
                  <div className="flex-1" />
                  <button
                    type="button"
                    onClick={() => setLeftMonth(leftMonth.add(1, 'month'))}
                    className="flex h-7 w-7 items-center justify-center rounded hover:bg-[#F3F4F6]"
                    aria-label="Próximo mês"
                  >
                    <ChevronRightIcon className="h-4 w-4 text-login-text-secondary" />
                  </button>
                </div>

                {/* Two calendars side by side */}
                <div className="flex gap-6">
                  {renderCalendar(leftMonth)}
                  {renderCalendar(rightMonth)}
                </div>

                {/* Actions */}
                <div className="mt-3 flex items-center justify-end gap-3 border-t border-[#E5E7EB] pt-3">
                  <button
                    type="button"
                    onClick={handleClear}
                    className="h-9 rounded-full border border-login-border-primary px-4 text-xs font-semibold text-login-text-secondary hover:bg-[#F3F4F6] transition-colors"
                  >
                    Limpar
                  </button>
                  <button
                    type="button"
                    onClick={handleApply}
                    className="h-9 rounded-full bg-login-btn-primary px-4 text-xs font-semibold text-black hover:opacity-90 transition-colors"
                  >
                    Aplicar
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
